import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import DataGrid from '../../components/data-grid/DataGrid';
import { useMsal } from '@azure/msal-react';
import Configuration from '../../config/einvoicing-configuration';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { DashboardColumns } from './DashboardColumns';
import { Loading } from '../../components/common/Loading';
import { PartnerIdToFriendlyNameMapping, CountryCodeToFriendlyNameMapping, DocumentTypeToFriendlyNameMapping, ModeToFriendlyNameMapping, StatusToFriendlyNameMapping, SupplierIdToFriendlyNameMapping, ChannelToFriendlyNameMapping, SalesMotionToFriendlyNameMapping } from '../../utils/FriendlyNameMappings';
import { mapPaymentType } from './DashboardMappingUtils';
import FilterPanel from '../../components/search-bar/FilterPanel';
import { DocumentSearchParams } from './DocumentSearchParams';
import { ErrorState } from '../../components/common/ErrorState';
import { Button, Switch, Label, makeStyles, tokens, shorthands, Title3, Text, Link, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components';
import { ArrowDownloadRegular, ArrowClockwiseRegular, OpenRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import { constructDocumentSearchUrl, constructExportSearchUrl, constructExportFailedDocumentsUrl, constructPostRequest, constructSearchPostRequest, getDefaultSearchParams } from '../../controller/dashboardController';
import { usePageTitle } from '../../hooks/usePageTitle';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        ...shorthands.padding('24px'),
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.gap('20px'),
        boxSizing: 'border-box',
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        boxShadow: tokens.shadow4,
        ...shorthands.padding('20px'),
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('16px'),
    },
    gridContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    gridHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    }
});

interface DashboardProps { 
    isAdmin?: boolean;
}

function Dashboard(props: DashboardProps) {
usePageTitle('Transaction Dashboard');
const styles = useStyles();
const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState('');
    const [searchParams, setSearchParams]: [DocumentSearchParams, React.Dispatch<React.SetStateAction<DocumentSearchParams>>] = useState(getDefaultSearchParams());
    const [searchResults, setSearchResults]: [any, React.Dispatch<React.SetStateAction<any>>] = useState([]);
    const [facetResults, setFacetResults]: [any[], React.Dispatch<React.SetStateAction<any[]>>] = useState<any[]>([]);
    const [initialFacetResults, setInitialFacetResults]: [any[], React.Dispatch<React.SetStateAction<any[]>>] = useState<any[]>([]);
    const [isLoading, setIsLoading]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState(true);
    const [exactSearch, setExactSearch]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState(true);
    const [useUTCTimeZone, setUseUTCTimeZone]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState(true);
    const [isError, setIsError]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState(false);
    const [totalCount, setTotalCount]: [number, React.Dispatch<React.SetStateAction<number>>] = useState(0);

    // DEV-ONLY: dummy payment types and invoice totals for local testing
    const dummyPaymentTypes = ['Cheque/Wire', 'Credit Card', 'ACH', 'SEPA', 'UPI'];
    const dummyCurrencies = ['USD', 'EUR', 'GBP', 'SEK', 'NOK', 'DKK', 'HUF', 'INR', 'BRL', 'SAR'];
    function getRandomPaymentType(): string {
        return dummyPaymentTypes[Math.floor(Math.random() * dummyPaymentTypes.length)];
    }

    function mapToDocumentRows(raw: any[]): any[] {
        return raw.map(row => {
            const mappedPaymentType = mapPaymentType(row.paymentType);
            return {
                documentId: row.documentId,
                documentType: DocumentTypeToFriendlyNameMapping[row.documentType],
                status: StatusToFriendlyNameMapping[row.status],
                errorCode: row.errorCode,
                extReceiptId: row.externalReceiptId,
                batchId: row.batchId,
                countryCode: CountryCodeToFriendlyNameMapping[row.countryCode] ?? row.countryCode,
                companyCode: row.companyCode,
                eInvPartnerId: PartnerIdToFriendlyNameMapping[row.eInvPartnerId],
                startEventTime: getFormattedDate(new Date(row.startEventTime)),
                endEventTime: (!row.endEventTime || row.endEventTime.includes('0001')) ? undefined : getFormattedDate(new Date(row.endEventTime)),
                internalInvoiceNumber: row.internalInvoiceNumber,
                state: row.state,
                supplier: SupplierIdToFriendlyNameMapping[row.supplier],
                mode: ModeToFriendlyNameMapping[row.mode],
                documentGenerationDate: getFormattedDate(new Date(row.documentGenerationDate)),
                invoiceNumber: row.invoiceNumber,
                scenario: row.scenario,
                channel: ChannelToFriendlyNameMapping[row.channel],
                salesMotion: SalesMotionToFriendlyNameMapping[row.salesMotion],
                numberOfSubmissions: row.numberOfSubmissions,
                paymentType: mappedPaymentType ?? (process.env.NODE_ENV === 'development' ? getRandomPaymentType() : undefined),
                invoiceAmount: row.invoiceAmount ?? (process.env.NODE_ENV === 'development' ? (Math.random() * 50000 + 100).toFixed(2) : undefined),
                invoiceCurrency: row.invoiceCurrency ?? (process.env.NODE_ENV === 'development' ? dummyCurrencies[Math.floor(Math.random() * dummyCurrencies.length)] : undefined)
            };
        })
    }
    function getFormattedDate(date: Date): string {
        if (useUTCTimeZone) {

            return date.getUTCFullYear() + '-' + (date.getUTCMonth() + 1) + '-' + date.getUTCDate() + ' ' + String(date.getUTCHours()).padStart(2, '0') + ':' + String(date.getUTCMinutes()).padStart(2, '0') + ':' + String(date.getUTCSeconds()).padStart(2, '0')
        }
        else {

            return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate() + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0') + ':' + String(date.getSeconds()).padStart(2, '0')
        }
    }

    // since errorCode facet is inside the errors object, update dimension for frontend code
    function reformatDimensions(json: any[]) {
        for (let item of json) {
            if (item.dimension === "errors/errorCode") { item.dimension = "errorCode" }
        }
    }

    async function getAccessToken() {
        if (accessToken === '') {
            const request = {
                ...Configuration.msalTokenRequest,
                account: accounts[0]
            };
            try {
                const result: AuthenticationResult = await instance.acquireTokenSilent(request);
                setAccessToken(result.idToken);
                return result.idToken;
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    await instance.acquireTokenRedirect(request);
                    return '';
                }
                throw error;
            }
        }
        return accessToken;
    }
    function useDocumentSearch(params: DocumentSearchParams) {
        useEffect(() => {
            const searchUrl: string = constructDocumentSearchUrl(Configuration.eventServiceUrl, params.startTime, params.endTime, params.searchText);
            async function startDocumentSearch() {
                if (!ignore) {
                    try {
                        const requestParams = constructSearchPostRequest(params, exactSearch, await getAccessToken());
                        const result = await fetch(searchUrl, requestParams);
                        if (result !== undefined && result.ok) {
                            const resultJson = await result.json()
                            let mappedRows = mapToDocumentRows(resultJson.documents);
                            reformatDimensions(resultJson.facets);
                            setFacetResults(resultJson.facets)
                            const hasActiveFilters = Object.values(params.filters).some((arr: string[]) => arr.length > 0);
                            if (initialFacetResults.length === 0 || !hasActiveFilters) {
                                setInitialFacetResults(resultJson.facets)
                            }
                            setSearchResults(mappedRows);
                            if (resultJson.totalCount != null) {
                                setTotalCount(resultJson.totalCount);
                            }
                            setIsLoading(false);
                        }
                        else {
                            setIsLoading(false);
                            setIsError(true);
                        }
                    }
                    catch (ex) {
                        setIsLoading(false);
                        setIsError(true);
                    }
                }
            }

            let ignore = false;
            startDocumentSearch();
            return () => {
                ignore = true;
            }
        }, [params]);
    }

    async function getExportSearch() {
        const searchUrl: string = constructExportSearchUrl(Configuration.eventServiceUrl, searchParams.startTime, searchParams.endTime, searchParams.searchText);

        try {
            const requestParams = constructPostRequest(searchParams, await getAccessToken());
            const result = await fetch(searchUrl, requestParams);
            if (result !== undefined && result.ok) {
                var blob = await result.blob();
                saveAs(blob, `searchExport.csv`);
            }
        }
        catch (ex) {
        }
    }

    async function getExportFailedDocuments() {
        const searchUrl: string = constructExportFailedDocumentsUrl(Configuration.eventServiceUrl, searchParams.startTime, searchParams.endTime, searchParams.searchText);

        try {
            const requestParams = constructPostRequest(searchParams, await getAccessToken());
            const result = await fetch(searchUrl, requestParams);
            if (result !== undefined && result.ok) {
                var blob = await result.blob();
                saveAs(blob, `failedDocumentsExport.csv`);
            }
        }
        catch (ex) {
        }
    }
    useDocumentSearch(searchParams);

    function refresh() {
        setSearchParams({ ...searchParams, page: 0 });
    }
    function onTimeZoneChange(event: React.ChangeEvent<HTMLInputElement>) {
        setUseUTCTimeZone(event.target.checked)
        refresh()
    }

    function mergeFacets(facetResults: any[], initialFacetResults: any[]): any[] {
        for (const facet of facetResults) {
            let initialFacetVersion = initialFacetResults.find(a => a.dimension === facet.dimension);
            for (const key in initialFacetVersion.aggregatedTotals) {
                if (!(key in facet.aggregatedTotals)) {
                    facet.aggregatedTotals[key] = 0;
                }
            }
        }
        return facetResults;
    }

    if (isLoading) {
        return <Loading label='Loading Documents...' />
    }
    else if (!isError) {
        return (
            <div className={styles.root}>
                <div className={styles.card}>
                    <FilterPanel searchParams={searchParams} setSearchParams={setSearchParams} facetResults={mergeFacets(facetResults, initialFacetResults)} useUTC={useUTCTimeZone} setExactSearch={setExactSearch}></FilterPanel>
                </div>
                
                <div className={`${styles.card} ${styles.gridContainer}`}>
                    <div className={styles.gridHeader}>
                        <Title3 role="status" aria-live="polite">Found {totalCount} Documents</Title3>
                        <div className={styles.controls}>
                            <Label>Time Zone:</Label>
                            <Switch label={useUTCTimeZone ? "UTC" : "Local"} onChange={onTimeZoneChange} checked={useUTCTimeZone} aria-description="Toggle between UTC and local time zone for displaying document timestamps" />
                            <Button icon={<ArrowClockwiseRegular />} title="Refresh" aria-label="Refresh" aria-description="Reload the document search results" onClick={refresh} appearance="subtle">Refresh</Button>
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <Button icon={<ArrowDownloadRegular />} title="Export" aria-label="Export" aria-description="Export search results as CSV" appearance="subtle">Export</Button>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem onClick={getExportSearch}>Export All</MenuItem>
                                        <MenuItem onClick={getExportFailedDocuments}>Export Failed</MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        </div>
                    </div>
                    <DataGrid
                        columns={DashboardColumns}
                        data={searchResults}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                        totalCount={totalCount}
                        isAdmin={props.isAdmin}
                    />
                </div>
            </div>
        );
    }
    else {
        return (
            <ErrorState
                action={{
                    label: 'Refresh page',
                    onClick: function noRefCheck() {
                        window.location.reload();
                    }
                }}
                description={
                    <>
                        <Text size={400}>Try refreshing the page. If access is granted recently, Logout & Login. </Text>
                        <Text size={400}>To request access to dashboard, please follow the TSG:  </Text>
                        <Button 
                            appearance="primary"
                            icon={<OpenRegular />}
                            iconPosition="after"
                            aria-label="Get Access to Dashboard"
                            aria-description="Open the TSG documentation to request access to the e-Invoicing dashboard"
                            onClick={() => window.open('https://eng.ms/docs/office-of-coo/commerce-ecosystems/commerce-financial-platforms/fp-tax-nonsox/modern-commerce-e-invoicing-ei/e-invoicing-tsgs/dashboard/get-access-to-einv-dashboard', '_blank')}
                        >
                            Get Access to Dashboard
                        </Button>
                        <Text size={400}>
                            If the issue persists, contact support at  {' '}
                            <Link href="mailto:einvoicing@microsoft.com">einvoicing@microsoft.com</Link>
                            {' '}  with details.
                        </Text>
                    </>
                }
                title="There was an issue loading the page"
            />);
    }
}

export default Dashboard;